/* eslint-disable react/no-string-refs */
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import React, { Component } from 'react';
import { PropTypes as PT } from 'prop-types';
import SVG from 'react-inlinesvg';
import handIcon from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import personLockIcon from '@lifesize/ux-assets/vectors/svgIcons/person-lock.svg';
import personPlusIcon from '@lifesize/ux-assets/vectors/svgIcons/person-plus.svg';
import chatIcon from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import collaborationIcon from '@lifesize/ux-assets/vectors/svgIcons/collaboration-tools.svg';
import settingsIcon from '@lifesize/ux-assets/vectors/svgIcons/settings.svg';
import infoIcon from '@lifesize/ux-assets/vectors/svgIcons/information-outline.svg';
import peopleIcon from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import InCallParticipantListContainer from 'containers/InCall/InCallParticipantList/InCallParticipantListContainer';
import InCallChatContainer from 'containers/InCall/InCallChat/InCallChatContainer';
import AddParticipantDrawer from 'containers/AddParticipantDrawer/AddParticipantDrawerContainer';
import CallDetailsDrawer from 'containers/InCall/InCallCallDetails/InCallCallDetailsContainer';
import CollaborationToolsDrawer from 'containers/InCall/InCallCollaboration/InCallCollaborationContainer';
import Tooltip from 'components/Tooltip/Tooltip';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _pickBy from 'lodash/pickBy';
import Badge from 'components/Badge/Badge';
import { participantFilterType } from 'constants/ParticipantList';
import {
  CALL_DETAILS_DRAWER,
  CHAT_DRAWER,
  COLLABORATION_DRAWER,
  PARTICIPANTS_DRAWER
} from 'constants/InCall/InCallDrawer';
import { ADD_PARTICIPANT_DRAWER } from 'constants/AddParticipantDrawer';
import classes from './InCallDrawerHolder.module.scss';

/**
 * This component will follow a similar paradigm to the FullScreenModal.
 * Subcomponents will be passed to the InCallDrawer. This will likely be a
 * separate child component of this holder, with the current contents being split
 * between drawer controls and drawercomponent.
 */

const messages = defineMessages({
  closeDrawer: {
    id: 'components.inCall.drawer.closeDrawerLabel',
    defaultMessage: 'Close Drawer'
  },
  addParticipantDrawer: {
    id: 'components.inCall.drawer.addParticipantDrawerButton',
    defaultMessage: 'Add a participant'
  },
  callDetailsDrawer: {
    id: 'components.inCall.drawer.callDetailsDrawerButton',
    defaultMessage: 'Call details'
  },
  participantsDrawer: {
    id: 'components.inCall.drawer.participantsDrawerButton',
    defaultMessage: 'Participant list'
  },
  chatDrawer: {
    id: 'components.inCall.drawer.chatDrawerButton',
    defaultMessage: 'Chat'
  },
  collaborationDrawer: {
    id: 'components.inCall.drawer.collaborationDrawerButton',
    defaultMessage: 'Collaboration Tools'
  },
  inCallSettings: {
    id: 'components.inCall.drawer.inCallSettings',
    defaultMessage: 'Camera and audio sources'
  }
});

export class InCallDrawerHolder extends Component {
  constructor(props) {
    super(props);

    this.state = {
      participantFilter: participantFilterType.none
    };
  }

  componentDidMount() {
    if (!this.props.isGuest) {
      this.props.onOpenClicked(CALL_DETAILS_DRAWER);
      if (this.props.checkForCrossGroup) {
        this.props.requestRemoteGroupUuid(this.props.extension);
      }
    }
  }

  componentWillUnmount() {
    this.props.clearRemoteGroupUuid();
    this.props.onCloseClicked();
  }

  selectParticipantFilter = (participantFilter) => {
    this.setState({
      participantFilter
    });
  }

  buttons = () => {
    const { isLockedByModerator } = this.props;
    const buttonGenerator = (keys) => {
      const allButtons = {
        [ADD_PARTICIPANT_DRAWER]: {
          className: '',
          dataTestId: 'add-participant-drawer-btn',
          key: 0,
          label: this.props.intl.formatMessage(messages.addParticipantDrawer),
          name: 'addParticipant',
          svg: personPlusIcon
        },
        [CALL_DETAILS_DRAWER]: {
          className: '',
          dataTestId: 'call-details-drawer-btn',
          key: 1,
          label: this.props.intl.formatMessage(messages.callDetailsDrawer),
          name: 'information',
          svg: infoIcon
        },
        [PARTICIPANTS_DRAWER]: {
          className: '',
          dataTestId: 'participants-drawer-btn',
          key: 2,
          label: this.props.intl.formatMessage(messages.participantsDrawer),
          name: 'participants',
          svg: isLockedByModerator ? personLockIcon : peopleIcon,
        },
        [CHAT_DRAWER]: {
          className: '',
          dataTestId: 'chat-drawer-btn',
          key: 3,
          label: this.props.intl.formatMessage(messages.chatDrawer),
          name: 'chat',
          svg: chatIcon
        },
        [COLLABORATION_DRAWER]: {
          className: '',
          dataTestId: 'collaboration-drawer-btn',
          key: 4,
          label: this.props.intl.formatMessage(messages.collaborationDrawer),
          name: 'collaboration',
          svg: collaborationIcon
        }
      };

      return _pickBy(allButtons, (value, key) => (_find(keys, providedKey => key === providedKey)));
    };
    // buttons are listed here in order of appearance
    return buttonGenerator(this.props.availableDrawers);
  };

  renderCloseButton = () => {
    return (
      <button
        ref="closeDrawerButton"
        id="closeDrawerButton"
        className={classes.closeButton}
        aria-label={this.props.intl.formatMessage(messages.closeDrawer)}
        onClick={this.props.onCloseClicked}
      >
        <SVG src={cancelIcon} data-testid="cancelIcon" />
      </button>
    );
  };

  renderDrawer() {
    return (
      <div
        id="openDrawer"
        className={classes.drawerContainer}
      >
        <div className={classes.drawerContents}>
          <AddParticipantDrawer isVisible={this.props.visibleComponent === ADD_PARTICIPANT_DRAWER} />
          <CallDetailsDrawer isVisible={this.props.visibleComponent === CALL_DETAILS_DRAWER} />
          <InCallParticipantListContainer
            isVisible={this.props.visibleComponent === PARTICIPANTS_DRAWER}
            participantFilter={this.state.participantFilter}
            selectParticipantFilter={this.selectParticipantFilter}
          />
          <InCallChatContainer isVisible={this.props.visibleComponent === CHAT_DRAWER} />
          <CollaborationToolsDrawer isVisible={this.props.visibleComponent === COLLABORATION_DRAWER} />
          {this.renderCloseButton()}
        </div>
      </div>
    );
  }

  getParticipantsTooltipLabel = (defaultLabel, notification, raisedHandsCount) => {
    if (notification || raisedHandsCount) {
      return (
        <div className={classes.tooltipLabelWithIcon}>
          <SVG src={handIcon} />
          <div className={classes.message}>
            {!!notification ? (
              <FormattedMessage {...notification} />
            ) : (
              <FormattedMessage
                id="components.inCall.drawer.participantsDrawerButton.handsRaised"
                defaultMessage="There {handsRaised, plural, one {is} other {are}} {handsRaised, number} raised {handsRaised, plural, one {hand} other {hands}}"
                values={{ handsRaised: raisedHandsCount }}
              />
            )}
          </div>
        </div>
      );
    }
    return defaultLabel;
  }

  renderButtonLabel = (button) => {
    const { btnNotificationRaisedHand, raisedHandsCount, unreadChats } = this.props;
    if (button.name !== 'participants') {
      return (
        <>
          <Tooltip
            label={button.label}
            location="left"
            style={{ right: '100%', marginRight: 11 }}
          />
          <SVG src={button.svg} />
          {(button.name === 'chat' && unreadChats && this.props.visibleComponent !== 'CHAT_DRAWER') ?
            <Badge count={unreadChats} className={classes.badge} /> : null}
        </>
      );
    }
    return (
      <>
        <Tooltip
          label={this.getParticipantsTooltipLabel(button.label, btnNotificationRaisedHand, raisedHandsCount)}
          location="left"
          style={{ right: '100%', marginRight: 11 }}
          useAltStyle={!!btnNotificationRaisedHand || !!raisedHandsCount}
          visible={!!btnNotificationRaisedHand}
        />
        <SVG src={button.svg} />
        {(raisedHandsCount) ?
          <Badge count={raisedHandsCount} className={`${classes.badge} ${classes.raisedHands}`} /> : null}
      </>
    );
  };

  render() {
    const { visibleComponent } = this.props;

    return (
      <div
        id="inCallDrawer"
        className={`${classes.drawerMainContainer} ${visibleComponent ? classes.drawerOpen : ''}`}
      >
        <div className={classes.drawerControls} >
          {_map(this.buttons(), (button, keyName) => {
            const activeStyle = visibleComponent && (visibleComponent === keyName) ? classes.active : '';

            return (
              <button
                aria-label={button.label}
                className={`${button.className} ${classes.openButton} ${activeStyle}`}
                data-testid={button.dataTestId}
                key={keyName}
                onClick={() => {
                  this.props.onOpenClicked(keyName);
                }}
              >
                {this.renderButtonLabel(button)}
              </button>
            );
          })}
          <hr className={classes.horizontalRule} />
          <button
            className={`${classes.openButton} ${classes.settingsButton}`}
            data-testid="settings-drawer-btn"
            id="avSettingsButton"
            onClick={this.props.openAV}
            ref="avSettingsButton"
          >
            <SVG src={settingsIcon} data-testid="settingsIcon" />
            <Tooltip
              label={this.props.intl.formatMessage(messages.inCallSettings)}
              location="left"
              style={{ right: '100%', marginRight: 11 }}
            />
          </button>
        </div>
        {this.renderDrawer()}
      </div>
    );
  }
}

InCallDrawerHolder.propTypes = {
  availableDrawers: PT.array,
  btnNotificationRaisedHand: PT.object,
  checkForCrossGroup: PT.bool,
  children: PT.element,
  clearRemoteGroupUuid: PT.func,
  extension: PT.string,
  intl: PT.object,
  isGuest: PT.bool,
  isLockedByModerator: PT.bool,
  onCloseClicked: PT.func,
  onOpenClicked: PT.func,
  openAV: PT.func,
  raisedHandsCount: PT.number,
  requestRemoteGroupUuid: PT.func,
  unreadChats: PT.number,
  visibleComponent: PT.string
};

export default injectIntl(InCallDrawerHolder);
